import React, { useCallback, useState, useMemo } from 'react';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import { LabsConfirmationModal } from './components/confirmation-modal/labs-confirmation-modal';
import { LabsFeatureTable } from './components/feature-table/labs-feature-table';
import { LabsFeedbackModal } from './components/feedback-modal/labs-feedback-modal';
import { useLabsActions } from './hooks/use-labs-actions';
import { useLabsData } from './hooks/use-labs-data';
import styles from './labs-page.module.less';
import { messages } from './messages';
import { PickListsCustomized } from '../../../constants/features';
import { useEnablePickListHtmlEditor } from '../../../feature-flags/hooks/use-enable-pick-list-html-editor';
import { useModal } from '../../../hooks/use-modal';
import { SettingsPageTitle } from '../components/settings-page-title/settings-page-title';
import settingsPageStyle from '../settings-page.module.less';

/**
 * Main labs page component for managing experimental and beta features
 * @returns {React.ReactElement} The labs page component
 */
export const LabsPage = () => {
  const { displayableFeatures: allDisplayableFeatures, enabledFeatures, isLoading: labsIsLoading, error: labsError } = useLabsData();
  const { handleEnableFeature, handleDisableFeature, isFeatureUpdating, getOptimisticEnabledState } = useLabsActions();
  const isPickListHtmlEditorEnabled = useEnablePickListHtmlEditor();

  const displayableFeatures = useMemo(
    () => (isPickListHtmlEditorEnabled ? allDisplayableFeatures.filter((f) => f.id !== PickListsCustomized) : allDisplayableFeatures),
    [allDisplayableFeatures, isPickListHtmlEditorEnabled],
  );

  const { open: openConfirmationModal, isOpen: isConfirmationModalOpen, close: closeConfirmationModal } = useModal();
  const [confirmationFeature, setConfirmationFeature] = useState(null);

  const { open: openFeedbackModal, isOpen: isFeedbackModalOpen, close: closeFeedbackModal } = useModal();
  const [feedbackFeature, setFeedbackFeature] = useState(null);
  const [isDisableMode, setIsDisableMode] = useState(false);
  const [isConfirmationViewOnly, setIsConfirmationViewOnly] = useState(false);
  const [isEnableSuccess, setIsEnableSuccess] = useState(false);

  const optimisticEnabledFeatures = useMemo(() => {
    const optimisticEnabled = new Set(enabledFeatures);

    displayableFeatures.forEach((feature) => {
      const optimisticState = getOptimisticEnabledState(feature.id);
      if (optimisticState == null) return;

      if (optimisticState) {
        optimisticEnabled.add(feature.id);
      } else {
        optimisticEnabled.delete(feature.id);
      }
    });

    return optimisticEnabled;
  }, [enabledFeatures, displayableFeatures, getOptimisticEnabledState]);

  const handleFeatureToggle = useCallback(
    (feature, isCurrentlyEnabled) => {
      if (isCurrentlyEnabled) {
        setFeedbackFeature(feature);
        setIsDisableMode(true);
        openFeedbackModal();
      } else {
        setConfirmationFeature(feature);
        setIsConfirmationViewOnly(false);
        setIsEnableSuccess(false);
        openConfirmationModal();
      }
    },
    [openFeedbackModal, openConfirmationModal],
  );

  const handleViewDetails = useCallback(
    (feature) => {
      setConfirmationFeature(feature);
      setIsConfirmationViewOnly(optimisticEnabledFeatures.has(feature.id));
      setIsEnableSuccess(false);
      openConfirmationModal();
    },
    [openConfirmationModal, optimisticEnabledFeatures],
  );

  const handleConfirmEnable = useCallback(async () => {
    try {
      const isSuccess = await handleEnableFeature(confirmationFeature);
      if (isSuccess) {
        setIsConfirmationViewOnly(true);
        setIsEnableSuccess(true);
      } else {
        closeConfirmationModal();
      }
    } catch {
      closeConfirmationModal();
    }
  }, [handleEnableFeature, confirmationFeature, closeConfirmationModal]);

  const handleCloseConfirmationModal = useCallback(() => {
    closeConfirmationModal();
    setIsEnableSuccess(false);
  }, [closeConfirmationModal]);

  const handleDisableWithFeedback = useCallback(
    async (feature, feedback) => {
      closeFeedbackModal();
      await handleDisableFeature(feature, feedback);
    },
    [handleDisableFeature, closeFeedbackModal],
  );

  const handleSendFeedback = useCallback(
    (feature) => {
      setFeedbackFeature(feature);
      setIsDisableMode(false);
      openFeedbackModal();
    },
    [openFeedbackModal],
  );

  const renderPrimaryContent = () => {
    if (labsError) {
      return (
        <FlexColumn gap={Gap.Space16}>
          <Text textType={TextType.Description} value={messages.errorLoadingFeatures} />
        </FlexColumn>
      );
    }

    if (!labsIsLoading && displayableFeatures.length === 0) {
      return (
        <FlexColumn gap={Gap.Space16}>
          <Text textType={TextType.Description} value={messages.noLabsFeaturesAvailableHeader} />
          <Text textType={TextType.Paragraph} value={messages.noLabsFeaturesAvailableDescription} />
        </FlexColumn>
      );
    }

    return (
      <LabsFeatureTable
        enabledFeatures={optimisticEnabledFeatures}
        features={displayableFeatures}
        isLoading={labsIsLoading}
        onSendFeedback={handleSendFeedback}
        onToggle={handleFeatureToggle}
        onViewDetails={handleViewDetails}
      />
    );
  };

  return (
    <div className={settingsPageStyle.content}>
      <div className={styles.labsPageContent}>
        <SettingsPageTitle value={messages.labsPageTitle} />
        <FlexColumn gap={Gap.Space24}>
          <Text textType={TextType.Paragraph} value={messages.labsPageDescription} />
          <div className={styles.warningBannerContainer}>
            <BannerMessageV2
              content={<Text textType={TextType.Paragraph} value={messages.labsWarningBanner} />}
              layout={BannerLayout.Default}
              status={BannerStatus.Warning}
            />
          </div>
          <FlexColumn gap={Gap.Space32}>{renderPrimaryContent()}</FlexColumn>
        </FlexColumn>
      </div>

      {isFeedbackModalOpen ? (
        <LabsFeedbackModal
          feature={feedbackFeature}
          isDisableMode={isDisableMode}
          isLoading={feedbackFeature ? isFeatureUpdating(feedbackFeature) : false}
          onClose={closeFeedbackModal}
          onDisableFeature={handleDisableWithFeedback}
        />
      ) : null}

      {isConfirmationModalOpen ? (
        <LabsConfirmationModal
          feature={confirmationFeature}
          isEnableSuccess={isEnableSuccess}
          isEnabling={!isConfirmationViewOnly}
          isLoading={confirmationFeature ? isFeatureUpdating(confirmationFeature) : false}
          isViewOnly={isConfirmationViewOnly}
          onCancel={handleCloseConfirmationModal}
          onConfirm={handleConfirmEnable}
        />
      ) : null}
    </div>
  );
};
